import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { getUrlParam } from 'core-components/helpers/url'
import { useCurrentSitePage } from 'core-components/sitebuilder/layout/hooks/useCurrentSitePage'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { sendAnalytics as sendData } from 'src/core/analytics'
import { logMessage } from 'src/core/analytics/helpers/logMessage'
import Logger from 'src/core/utils/Logger'

interface IWidgetTracker {
  widgets: { [key: string]: boolean }
  timer: number
  processedPage: string
}

export const useWidgetAnalytics = () => {
  const currentSitePage = useCurrentSitePage()
  const widgetMeasureRef = useRef<IWidgetTracker>({ widgets: {}, timer: 0, processedPage: '' })
  const user = useSelector(selectUser)

  const register = useCallback((name: string) => {
    widgetMeasureRef.current.widgets[name] = false
  }, [])

  const resetMeasure = useCallback(() => {
    widgetMeasureRef.current.widgets = {}
    widgetMeasureRef.current.timer = performance.now()
  }, [])

  const send = useCallback(
    (page: string, duration: number) => {
      if (page !== widgetMeasureRef.current.processedPage) {
        logMessage(
          `Page: ${page}. Duration: ${duration}. Widgets: ${Object.keys(widgetMeasureRef.current.widgets)}`
        )

        sendData(
          {
            objectId: page,
            objectType: 'page',
            objectParams: {
              name: window.location.pathname,
              duration,
            },
          },
          user.email
        ).catch(Logger.error)

        resetMeasure()
        widgetMeasureRef.current.processedPage = page
      }
    },
    [resetMeasure, user.email]
  )

  const sendAnalytics = useCallback(
    (name: string) => {
      widgetMeasureRef.current.widgets[name] = true
      const isWidgetFinished = Object.values(widgetMeasureRef.current.widgets).every(Boolean)
      const duration = Math.floor(performance.now() - widgetMeasureRef.current.timer)
      const page = getUrlParam('page')
      if (isWidgetFinished) {
        send(page, duration)
      }
    },
    [send]
  )

  useEffect(() => {
    const isWidgetNotFinished = Object.values(widgetMeasureRef.current.widgets).some((el) => !el)
    if (isWidgetNotFinished) {
      const duration = performance.now() - widgetMeasureRef.current.timer
      const MAX_DURATION = 30000
      if (duration > MAX_DURATION) {
        send(currentSitePage.id, duration)
      }
    }
    resetMeasure()
  }, [currentSitePage.id, resetMeasure, send])

  return { sendAnalytics, register }
}
